export interface Service {
  slug: string
  title: string
  summary: string
  intro: string
  includes: string[]
  suitedFor: string[]
}

export const services: Service[] = [
  {
    slug: 'trust-administration',
    title: 'Trust Administration',
    summary:
      'Managing trust assets, distributions, records, expenses and beneficiary matters in accordance with the terms of the trust.',
    intro:
      'We serve as trustee or work alongside a named trustee to carry out the terms of a trust exactly as written — handling the assets, the accounting and the communication with beneficiaries.',
    includes: [
      'Inventory and safekeeping of trust assets',
      'Distributions made in accordance with trust terms',
      'Bill payment, expense tracking and record keeping',
      'Annual accountings and reporting to beneficiaries',
      'Coordination with attorneys, CPAs and investment advisors',
    ],
    suitedFor: [
      'Families without a suitable individual to serve as trustee',
      'Trusts where neutrality between beneficiaries matters',
      'Successor trustees who need professional support',
    ],
  },
  {
    slug: 'special-needs-trust-administration',
    title: 'Special Needs Trust Administration',
    summary:
      'Managing trust assets and distributions for individuals with disabilities while coordinating with families and appropriate professional advisors.',
    intro:
      'Special needs trusts require distributions that support quality of life without disrupting eligibility for public benefits. We administer these trusts with that balance in mind.',
    includes: [
      'Distributions reviewed against benefit eligibility rules',
      'Coordination with case managers and care providers',
      'Detailed documentation of every disbursement',
      'Regular contact with family members and advocates',
      'Annual accountings for courts or interested parties',
    ],
    suitedFor: [
      'Parents planning for a child with a disability',
      'First-party trusts funded by a settlement',
      'Families needing continuity after a caregiver passes',
    ],
  },
  {
    slug: 'poa-ahcd-services',
    title: 'POA & AHCD Services',
    summary:
      'Assisting with financial affairs, personal matters, healthcare coordination and other responsibilities under properly established authority.',
    intro:
      'Acting under a power of attorney or advance health care directive, we step in to manage financial and healthcare decisions when someone is unable to manage them alone.',
    includes: [
      'Day-to-day financial management and bill payment',
      'Coordination with physicians and care teams',
      'Documentation of decisions made under the directive',
      'Regular updates to family members where appropriate',
      'Careful adherence to the scope of the granted authority',
    ],
    suitedFor: [
      'Individuals without nearby family to assist',
      'Families in conflict who need a neutral agent',
      'Anyone planning ahead for a period of incapacity',
    ],
  },
  {
    slug: 'conservatorship-services',
    title: 'Conservatorship Services',
    summary:
      'Serving as conservator of the person or estate under court appointment, with the reporting and oversight the role requires.',
    intro:
      'Conservatorship is a court-supervised role with strict reporting duties. We take it on with the organization, documentation and diligence the court expects.',
    includes: [
      'Court-appointed conservator of the person or estate',
      'Inventory and appraisal of estate assets',
      'Care planning, housing and medical coordination',
      'Court accountings prepared and filed on schedule',
      'Ongoing communication with counsel and the court',
    ],
    suitedFor: [
      'Adults who can no longer manage their own affairs',
      'Situations where family members cannot agree',
      'Courts seeking a professional, neutral appointee',
    ],
  },
  {
    slug: 'probate-administration',
    title: 'Probate Administration',
    summary:
      'Administering estates through probate — locating assets, addressing debts and distributing property to the rightful heirs.',
    intro:
      'We serve as personal representative or assist an appointed executor through each stage of probate, from the initial inventory through final distribution.',
    includes: [
      'Locating, securing and valuing estate assets',
      'Notice to creditors and resolution of valid claims',
      'Coordination of tax filings with the estate CPA',
      'Court filings and required accountings',
      'Final distribution and closing of the estate',
    ],
    suitedFor: [
      'Estates with no willing or available executor',
      'Executors living out of state',
      'Estates with complex assets or multiple heirs',
    ],
  },
  {
    slug: 'personal-fiduciary-care',
    title: 'Personal & Private Fiduciary Care',
    summary:
      'Ongoing help with personal affairs — mail, bills, records, appointments and the routine administration that keeps life in order.',
    intro:
      'Some clients simply need a dependable professional handling the paperwork of daily life. This service is practical, quiet and organized around the client.',
    includes: [
      'Mail review, bill payment and account reconciliation',
      'Organized records and document safekeeping',
      'Insurance, benefit and vendor correspondence',
      'Appointment scheduling and household coordination',
      'Regular written summaries of activity',
    ],
    suitedFor: [
      'Older adults who prefer to remain independent',
      'Clients recovering from illness or injury',
      'Families living at a distance from a parent',
    ],
  },
  {
    slug: 'care-management',
    title: 'Care Management',
    summary:
      'Assessing needs, arranging services and monitoring care so that support stays appropriate as circumstances change.',
    intro:
      'Care rarely stays the same for long. We assess what is needed now, arrange it, and keep watch so adjustments happen before a situation becomes urgent.',
    includes: [
      'In-person assessment of needs and living situation',
      'Vetting and coordination of in-home caregivers',
      'Attendance at medical appointments when needed',
      'Placement research for higher levels of care',
      'Written care plans reviewed on a set schedule',
    ],
    suitedFor: [
      'Families managing care from another city',
      'Clients whose needs are changing quickly',
      'Trustees responsible for a beneficiary’s wellbeing',
    ],
  },
  {
    slug: 'estate-administration',
    title: 'Estate Administration',
    summary:
      'Settling an estate outside of probate — coordinating assets, taxes, distributions and the many details that follow a death.',
    intro:
      'After a death there is a long list of administrative work. We take that list on, keeping beneficiaries informed and the process moving in order.',
    includes: [
      'Asset consolidation and account transitions',
      'Coordination of appraisals and property sales',
      'Debt, tax and final expense resolution',
      'Beneficiary communication and reporting',
      'Distribution per the governing document',
    ],
    suitedFor: [
      'Successor trustees new to the role',
      'Beneficiaries who prefer a neutral administrator',
      'Estates with property in more than one state',
    ],
  },
]

export function getService(slug: string): Service | undefined {
  return services.find((service) => service.slug === slug)
}
